/**
 * CrewSchedule — who's on which job this week. Data: GET /api/crew-schedule
 * Crew is assigned on each deal (Deal page → Crew card).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { HardHat, ChevronLeft, ChevronRight } from "lucide-react";

const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export default function CrewSchedule() {
  const [start, setStart] = useState("");
  const [data, setData] = useState(null);
  const load = (s) => api.get(`/crew-schedule${s ? `?start=${s}` : ""}`).then((r) => {
    setData(r.data);
    setStart(r.data.week_start);
  }).catch(() => setData({ jobs: [] }));
  useEffect(() => { load(""); }, []);

  return (
    <div className="p-6 lg:p-8" data-testid="crew-schedule-page">
      <div className="flex items-center gap-3 mb-2">
        <HardHat className="w-6 h-6 text-blue-700" />
        <h1 className="font-heading text-2xl font-black tracking-tight">Crew Schedule</h1>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Jobs scheduled this week and who's assigned. Assign crew on each deal's Crew card.</p>

      <div className="flex items-center gap-2 mb-5">
        <button onClick={() => load(addDays(start, -7))} className="h-8 px-2 border border-zinc-300 rounded-sm hover:border-zinc-950" data-testid="crew-prev-week"><ChevronLeft className="w-4 h-4" /></button>
        <span className="font-mono text-sm font-bold" data-testid="crew-week-label">{data?.week_start} → {data?.week_end}</span>
        <button onClick={() => load(addDays(start, 7))} className="h-8 px-2 border border-zinc-300 rounded-sm hover:border-zinc-950" data-testid="crew-next-week"><ChevronRight className="w-4 h-4" /></button>
        <button onClick={() => load("")} className="h-8 px-3 border border-zinc-300 rounded-sm text-[10px] font-bold uppercase tracking-wider hover:border-zinc-950" data-testid="crew-this-week">This Week</button>
      </div>

      {data && data.jobs.length === 0 && (
        <div className="bg-white border border-dashed border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500" data-testid="crew-empty">
          No jobs scheduled this week. Set start/end dates on a deal to see it here.
        </div>
      )}

      {/* Day-by-day grid: Mon–Sun columns, each cell = jobs active that day + crew */}
      {data && data.jobs.length > 0 && (
        <div className="overflow-x-auto mb-6" data-testid="crew-day-grid">
          <div className="grid grid-cols-7 gap-2 min-w-[980px]">
            {[...Array(7)].map((_, i) => {
              const day = addDays(data.week_start, i);
              const isToday = day === new Date().toISOString().slice(0, 10);
              const dayJobs = data.jobs.filter((j) => j.start <= day && day <= j.end);
              return (
                <div key={day} className={`border rounded-sm ${isToday ? "border-blue-700 bg-blue-50/40" : "border-zinc-200 bg-white"}`} data-testid={`crew-day-${day}`}>
                  <div className={`px-2 py-1.5 border-b text-center ${isToday ? "border-blue-200 bg-blue-700 text-white" : "border-zinc-100 bg-zinc-50"}`}>
                    <div className="text-[10px] font-bold uppercase tracking-wider">{new Date(day + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" })}</div>
                    <div className={`font-mono text-[11px] ${isToday ? "text-blue-100" : "text-zinc-500"}`}>{new Date(day + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                  </div>
                  <div className="p-1.5 space-y-1.5 min-h-[70px]">
                    {dayJobs.length === 0 && <div className="text-[10px] text-zinc-300 text-center pt-3">—</div>}
                    {dayJobs.map((j) => (
                      <div key={j.deal_id} className="border border-zinc-200 rounded-sm p-1.5 bg-white">
                        <Link to={`/projects/${j.deal_id}`} className="block text-[11px] font-bold leading-tight hover:text-blue-700 truncate" title={j.title}>{j.title}</Link>
                        {j.crew.length === 0 ? (
                          <div className="text-[9px] font-bold uppercase text-amber-700 mt-0.5">No crew</div>
                        ) : (
                          <div className="flex flex-wrap gap-0.5 mt-1">
                            {j.crew.map((m) => (
                              <span key={m.id} className="text-[9px] font-bold bg-zinc-100 border border-zinc-200 rounded-sm px-1 py-0.5">{m.name}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Compact job list under the grid */}
      <div className="space-y-3">
        {(data?.jobs || []).map((j) => (
          <div key={j.deal_id} className="bg-white border border-zinc-200 rounded-sm px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`crew-job-${j.deal_id}`}>
            <div className="min-w-[220px]">
              <Link to={`/projects/${j.deal_id}`} className="font-bold hover:text-blue-700">{j.title}</Link>
              <div className="font-mono text-[11px] text-zinc-500">{j.start} → {j.end}</div>
            </div>
            <div className="flex flex-wrap gap-1.5 ml-auto">
              {j.crew.length === 0 && <span className="text-[10px] font-bold uppercase bg-amber-50 text-amber-800 border border-amber-300 rounded-sm px-2 py-1">No crew assigned</span>}
              {j.crew.map((m) => (
                <span key={m.id} className="text-[11px] font-bold bg-zinc-100 border border-zinc-200 rounded-sm px-2 py-1">{m.name}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
